var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { SceneGraphNode, HumanObjectTransform, HumanObjectRenderable } from '../../models/scene-graph-node';
import { renamespaceValue } from '../renamespace-utils';
var EPS = 1e-4;
export var SerializeGraphUtils;
(function (SerializeGraphUtils) {
    function filterEuclidean(data, target) {
        if (target === void 0) { target = 0; }
        ['x', 'y', 'z'].forEach(function (dimension) {
            if (data[dimension] === undefined) {
                return;
            }
            if (Math.abs(data[dimension] - target) < EPS) {
                delete data[dimension];
            }
        });
    }
    function filterTransformProperties(transform) {
        ['translate', 'rotate', 'pivot'].forEach(function (property) {
            if (transform[property] === undefined) {
                return;
            }
            filterEuclidean(transform[property]);
            if (Object.keys(transform[property]).length === 0) {
                delete transform[property];
            }
        });
        if (transform.scale === undefined) {
            return;
        }
        filterEuclidean(transform.scale, 1.0);
        if (Object.keys(transform.scale).length === 0) {
            delete transform.scale;
        }
    }
    SerializeGraphUtils.filterTransformProperties = filterTransformProperties;
    // serialize non-default values on human object properties
    function serializeObjectProperties(humanObject, node) {
        ['backfaces', 'displayName', 'layer'].forEach(function (property) {
            if (humanObject[property]) {
                node[property] = humanObject[property];
            }
        });
    }
    SerializeGraphUtils.serializeObjectProperties = serializeObjectProperties;
    function serializeTransformData(humanObject, node) {
        var transform = new HumanObjectTransform();
        transform.translate = JSON.parse(JSON.stringify(humanObject.translate));
        transform.rotate = JSON.parse(JSON.stringify(humanObject.rotate));
        transform.scale = JSON.parse(JSON.stringify(humanObject.scale));
        transform.pivot = JSON.parse(JSON.stringify(humanObject.pivot));
        filterTransformProperties(transform);
        Object.entries(transform).forEach(function (_a) {
            var _b = __read(_a, 2), transformType = _b[0], transformVal = _b[1];
            node[transformType] = JSON.parse(JSON.stringify(transformVal));
        });
        if (node.transformId) {
            delete node.transformId;
        }
    }
    SerializeGraphUtils.serializeTransformData = serializeTransformData;
    function serializeRenderables(humanObject, node, namespaceMap) {
        var assetTypes = ['geometryId', 'morphId', 'materialId'];
        var renderables = [];
        if (humanObject.renderables) {
            humanObject.renderables.forEach(function (renderTarget) {
                var renderable = new HumanObjectRenderable;
                // copy assets
                assetTypes.forEach(function (assetType) {
                    if (renderTarget[assetType]) {
                        renderable[assetType] = renamespaceValue(renderTarget[assetType], namespaceMap);
                    }
                });
                // other attributes: layer
                // TODO is this supposed to filter out layer's with 0?
                if (renderTarget.layer) {
                    renderable.layer = renderTarget.layer;
                }
                // add
                renderables.push(renderable);
            });
        }
        // serialize on to scene graph node
        if (renderables.length === 1) {
            node = __assign({}, node, renderables[0]);
        }
        else if (renderables.length > 1) {
            node.renderables = renderables;
        }
        return node;
    }
    SerializeGraphUtils.serializeRenderables = serializeRenderables;
    function serializeHumanObject(humanObject, namespaceMap, overwrite, excludeMap, replaceObjectId) {
        if (replaceObjectId === void 0) { replaceObjectId = false; }
        if (excludeMap[humanObject.objectId]) {
            return null;
        }
        var result = new SceneGraphNode();
        result.objectId = humanObject.objectId;
        // id
        // By default object IDs do not change.
        // The only time they would change is when making a deep copy of an entire model in scene editor.
        if (replaceObjectId) {
            Object.entries(namespaceMap)
                .forEach(function (_a) {
                var _b = __read(_a, 2), key = _b[0], value = _b[1];
                var regex = new RegExp("^" + key);
                result.objectId = result.objectId.replace(regex, value);
            });
        }
        // properties
        serializeObjectProperties(humanObject, result);
        // transform data
        if (overwrite || !humanObject.transformId) {
            serializeTransformData(humanObject, result);
        }
        else if (humanObject.transformId) {
            result.transformId = renamespaceValue(humanObject.transformId, namespaceMap);
        }
        // object renderables
        result = serializeRenderables(humanObject, result, namespaceMap);
        // downward traversal
        if (humanObject.objects && humanObject.objects.length) {
            result.objects = [];
        }
        humanObject.objects.forEach(function (childObject) {
            var childNode = serializeHumanObject(childObject, namespaceMap, overwrite, excludeMap, replaceObjectId);
            if (childNode !== null) {
                result.objects.push(childNode);
            }
        }, this);
        return result;
    }
    SerializeGraphUtils.serializeHumanObject = serializeHumanObject;
})(SerializeGraphUtils || (SerializeGraphUtils = {}));
